// Shared layout utilities for LogViewer and LogLineRenderer. These constants
// and functions ensure consistent sizing calculations.

import {MEDIUM_BREAKPOINT} from '../../css/breakpoints.styles';
import {MAX_VISIBLE_LINE_LENGTH} from './constants';

/**
 * Horizontal padding applied by the 'Alias' component (timestamps/labels).
 */
export const ALIAS_PADDING_X_PX = 12;

/**
 * Horizontal padding for each log line container.
 */
export const LINE_PADDING_X_PX = 32;

/**
 * Vertical padding for each log line.
 */
export const LINE_PADDING_Y_PX = 4;

/**
 * Gap between flex items in log lines.
 */
export const LINE_GAP_PX = 8;

/**
 * Timestamp format character count: "YYYY-MM-DD-HH-MM-SS"
 */
export const TIMESTAMP_CHAR_COUNT = 19;

/**
 * Minimum width (in pixels) for content before it wraps to its own line.
 * This threshold prevents content from being squeezed into unusably narrow space.
 */
export const MIN_CONTENT_WIDTH_PX = 100;

export interface CalculateLineHeightParams {
  /** Container width in pixels. */
  containerWidthPx: number;
  /** Measured character width in pixels. */
  charWidthPx: number;
  /** Line height in pixels (default or compact). */
  lineHeightPx: number;
  /** Raw log line content (will be normalized and truncated internally). */
  content: string;
  /** Maximum label length for alignment. */
  maxLabelLength: number;
  /** Maximum line number length for alignment. */
  maxLineNumLength: number;
  /** Whether labels are displayed. */
  showLabel: boolean;
  /** Whether line numbers are displayed. */
  showLineNumber: boolean;
  /** Whether timestamps are displayed. */
  showTimestamp: boolean;
}

/**
 * Calculates the estimated rendered height of a log line. This value includes
 * the log line number, timestamp, and label, too (accounting for visibility).
 *
 * The `LogLineRender` wraps differently on mobile screens (i.e. below the
 * medium breakpoint): timestamps, labels, and content can all be wrapped to the
 * next line if they don't fit.
 *
 * NOTE: This function is brittle and tightly coupled to the `LogLineRenderer`
 * implementation. It's unfortunate, but line height estimation is required for
 * smooth scrolling in the `LogViewer` given that logs can differ in height.
 *
 * TODO: This function does not account for the '\u00a0' (NBSP) characters that
 * are substituted in for spaces via `LogLineRenderer`. This results in wrong
 * calculations when a space would be the character that breaks the line.
 */
export function calculateLogLineHeight({
  containerWidthPx,
  charWidthPx,
  lineHeightPx,
  content,
  maxLabelLength,
  maxLineNumLength,
  showLabel,
  showLineNumber,
  showTimestamp,
}: CalculateLineHeightParams): number {
  // Collapse consecutive whitespace characters (newlines, tabs, etc.) to a
  // single space to match browser rendering, then truncate to max length.
  content = content
    .slice(0, MAX_VISIBLE_LINE_LENGTH)
    .replace(/[\r\n\t\v\f]+/g, ' ');
  const contentLength = Math.min(content.length, MAX_VISIBLE_LINE_LENGTH);

  const lineNumWidth = showLineNumber ? maxLineNumLength * charWidthPx : 0;
  const lineNumGap = lineNumWidth > 0 ? LINE_GAP_PX : 0;

  // The remaining width available to the timestamp, log label, and contents.
  const wrappableWidth =
    containerWidthPx - LINE_PADDING_X_PX - lineNumWidth - lineNumGap;

  const timestampWidth = showTimestamp
    ? TIMESTAMP_CHAR_COUNT * charWidthPx + ALIAS_PADDING_X_PX
    : 0;
  const labelWidth =
    showLabel && maxLabelLength > 0
      ? maxLabelLength * charWidthPx + ALIAS_PADDING_X_PX
      : 0;

  // NOTE: To support mobile, the wrapping behavior needs to be simulated to
  // correctly determine the number of wrapped lines.

  let widthCurrent = 0;
  let extraRows = 0;
  let wrapGaps = 0;

  // NOTE: It's assumed that the screen will always be able to render a log line
  // + timestamp. Given this is only about 250px, that should be reasonable.
  if (timestampWidth > 0) {
    widthCurrent = timestampWidth;
  }

  const canWrap = containerWidthPx < MEDIUM_BREAKPOINT;

  if (labelWidth > 0) {
    const widthNeeded =
      timestampWidth > 0 ? LINE_GAP_PX + labelWidth : labelWidth;

    if (canWrap && widthCurrent + widthNeeded > wrappableWidth) {
      extraRows++; // Labels will not split across multiple lines.
      wrapGaps++;
      widthCurrent = labelWidth;
    } else {
      widthCurrent += widthNeeded;
    }
  }

  // The width remaining on the current line for content (after metadata).
  const widthRemainingOnLine = Math.max(
    wrappableWidth - (widthCurrent > 0 ? LINE_GAP_PX : 0) - widthCurrent,
    MIN_CONTENT_WIDTH_PX
  );

  // On mobile, content can wrap to its own line if it doesn't fit alongside
  // metadata. On desktop, content stays on the same row and wraps within its
  // available space.
  const contentWidth = contentLength * charWidthPx;
  const contentOnOwnLine =
    canWrap && widthCurrent > 0 && contentWidth > widthRemainingOnLine;
  if (contentOnOwnLine) {
    extraRows++;
    wrapGaps++;
  }

  const contentWrapWidth = contentOnOwnLine
    ? wrappableWidth
    : widthRemainingOnLine;
  const charsPerLine = Math.max(Math.floor(contentWrapWidth / charWidthPx), 1);
  const contentRows = Math.max(Math.ceil(contentLength / charsPerLine), 1);

  const wrapGapHeight = wrapGaps * LINE_GAP_PX;
  return (
    (extraRows + contentRows) * lineHeightPx + LINE_PADDING_Y_PX + wrapGapHeight
  );
}
